import { z } from "zod";
import { applicationRoleSchema } from "./roles.js";
import {
  COPILOT_GRAPH_SCOPES_DEFAULT,
  COPILOT_RETRIEVAL_GRAPH_SCOPES_DEFAULT,
  COPILOT_UPLOAD_GRAPH_SCOPES_DEFAULT,
  COPILOT_WORKIQ_SCOPES_DEFAULT,
  GDAP_GRAPH_SCOPES_DEFAULT,
  WORK_IQ_BASE_URL_DEFAULT,
} from "./microsoft-scopes.js";

/**
 * A string whose canonical default lives in `microsoft-scopes.ts`.
 *
 * An EMPTY variable means "use the canonical default", not "request nothing":
 * `docker-compose.yml` interpolates `${VAR:-}` for every variable it passes, so
 * a plain `.default()` would be overridden by the empty string and the
 * deployment would come up missing permissions with nothing to show for it.
 * That is exactly how the Copilot scopes went absent from the compose path.
 */
const canonical = (fallback: string) =>
  z
    .string()
    .default(fallback)
    .transform((value) => value.trim() || fallback);

/**
 * Environment schema for apps/api. Validated once at startup so a
 * misconfigured deployment fails fast with a clear message instead of
 * surfacing as a confusing runtime error later.
 *
 * Authentication (Phase 3): a development auth mode with a configured local
 * user. Entra mode is recognized but intentionally unimplemented — selecting it
 * causes a clear startup error (handled by the auth provider factory), never a
 * silent fallback. No Entra IDs / secrets / tokens are defined here yet.
 */
export const apiEnvSchema = z.object({
  NODE_ENV: z
    .enum(["development", "test", "production"])
    .default("development"),
  PORT: z.coerce.number().int().positive().default(3000),
  LOG_LEVEL: z
    .enum(["fatal", "error", "warn", "info", "debug", "trace", "silent"])
    .default("info"),
  DATABASE_URL: z
    .string()
    .min(1, "DATABASE_URL is required (postgres://user:pass@host:port/db)"),

  // ── Authentication ──────────────────────────────────────────────────────
  // - development: single configured local user (local dev + tests only).
  // - local:       production username/password auth (Argon2 + JWT sessions).
  // - entra:       Microsoft Entra ID (recognized but NOT implemented).
  AUTH_MODE: z.enum(["development", "local", "entra"]).default("development"),

  // ── Module authorization rollout ────────────────────────────────────────
  //   AUTHZ_MODE  "shadow" evaluates every authorization decision and records
  //               it, then lets the legacy outcome stand. "enforce" denies.
  //
  // Defaults to "shadow" DELIBERATELY. Enforcement must never be switched on by
  // a deployment that merely forgot to set a variable — it is enabled once the
  // divergence report shows every would-be denial is intended. The
  // `authz.mode` row in application_settings overrides this at runtime, which is
  // what makes rollback possible without a deploy.
  //
  // Shadow mode is INSTRUMENTATION, NOT PROTECTION: while it is active the
  // module layer is not enforcing, and no deployment running it should be
  // described as if it were.
  AUTHZ_MODE: z.enum(["shadow", "enforce"]).default("shadow"),

  //   AUTHZ_ENFORCE_USER_IDS  Progressive enforcement cohort: internal user
  //               uuids, comma-separated. When set, ONLY these principals are
  //               enforced against and everyone else stays in shadow, so
  //               enforcement can be proven on a small group first. Empty means
  //               the mode applies to everybody.
  AUTHZ_ENFORCE_USER_IDS: z
    .string()
    .default("")
    .transform((v) =>
      v
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    ),

  // Production local-auth (Phase 5.8). Only meaningful when AUTH_MODE=local.
  //   AUTH_JWT_SECRET            HMAC key signing access tokens (HS256). Min 32
  //                              chars; never logged/returned. REQUIRED — there
  //                              is deliberately no default.
  //
  //                              SECURITY: this previously defaulted to a fixed
  //                              literal published in this repository. Any
  //                              deployment that never set the variable signed
  //                              its session tokens with a public key, so anyone
  //                              could forge an access token for any user and
  //                              any role. There is no default now: the process
  //                              refuses to start without a real value, and
  //                              `assertProductionSecrets` additionally refuses
  //                              to start production on the old placeholder.
  //   AUTH_ACCESS_TOKEN_TTL_SEC  access-token lifetime (default 15 min).
  //   AUTH_REFRESH_TOKEN_TTL_SEC refresh-token lifetime (default 14 days);
  //                              rememberMe uses this, otherwise a shorter
  //                              session-cookie lifetime. This is the ABSOLUTE
  //                              session horizon: it is fixed when the session is
  //                              created and refresh rotation never extends it.
  //   AUTH_IDLE_TIMEOUT_SEC      idle (inactivity) timeout, default 30 min. A
  //                              session whose last activity is older than this
  //                              is revoked on the next authenticated request or
  //                              refresh — the absolute horizon above is not
  //                              enough on its own (an unattended browser would
  //                              stay signed in for the full refresh lifetime).
  //   AUTH_COOKIE_SECURE         set the Secure flag on auth cookies (default
  //                              true; set false only for local http dev).
  //   AUTH_MAX_FAILED_LOGINS     failed logins before lockout (default 5).
  //   AUTH_LOCKOUT_MINUTES       lockout duration after the threshold (def 15).
  //   AUTH_LOGIN_RATE_MAX        max login attempts per IP per window (def 10).
  //   AUTH_LOGIN_RATE_WINDOW_SEC rate-limit window seconds (default 60).
  AUTH_JWT_SECRET: z
    .string()
    .min(
      32,
      "AUTH_JWT_SECRET must be at least 32 characters (set a long random value: openssl rand -base64 48)",
    ),
  AUTH_ACCESS_TOKEN_TTL_SEC: z.coerce.number().int().positive().default(900),
  AUTH_REFRESH_TOKEN_TTL_SEC: z.coerce
    .number()
    .int()
    .positive()
    .default(1_209_600),
  AUTH_IDLE_TIMEOUT_SEC: z.coerce.number().int().positive().default(1_800),
  AUTH_COOKIE_SECURE: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  AUTH_MAX_FAILED_LOGINS: z.coerce.number().int().positive().default(5),
  AUTH_LOCKOUT_MINUTES: z.coerce.number().int().positive().default(15),
  AUTH_LOGIN_RATE_MAX: z.coerce.number().int().positive().default(10),
  AUTH_LOGIN_RATE_WINDOW_SEC: z.coerce.number().int().positive().default(60),

  // Login rate limiting has three buckets, all counted in a SHARED store
  // (PostgreSQL — `rate_limit_counters`, migration 0025) so every API instance
  // sees the same totals. Defence in depth against the three distinct attacks:
  //   per-IP      (AUTH_LOGIN_RATE_MAX / _WINDOW_SEC above) — brute force from
  //               one address.
  //   per-account (AUTH_LOGIN_ACCOUNT_RATE_MAX) — credential stuffing against
  //               one identity from many addresses. Counted on a HASH of the
  //               normalized email, so the counter table holds no addresses.
  //   global      (AUTH_LOGIN_GLOBAL_RATE_MAX) — password spraying: many
  //               accounts, one attempt each, rotating source addresses. Sized
  //               well above the whole team's plausible peak; it is a
  //               circuit-breaker, not a quota.
  AUTH_LOGIN_ACCOUNT_RATE_MAX: z.coerce.number().int().positive().default(5),
  AUTH_LOGIN_ACCOUNT_RATE_WINDOW_SEC: z.coerce
    .number()
    .int()
    .positive()
    .default(300),
  AUTH_LOGIN_GLOBAL_RATE_MAX: z.coerce.number().int().positive().default(300),
  AUTH_LOGIN_GLOBAL_RATE_WINDOW_SEC: z.coerce
    .number()
    .int()
    .positive()
    .default(60),

  // ── Breached-password checking (Pwned Passwords, k-anonymity) ────────────
  // Checked whenever a local password is SET (the create-user script). Only the
  // first 5 hex characters of the password's SHA-1 are sent; the range response
  // is searched locally, so the password, its full hash and the account never
  // leave this process. `Add-Padding: true` is requested so the response size
  // does not leak how many suffixes matched the prefix.
  //   PASSWORD_BREACH_CHECK_ENABLED  set false ONLY for an air-gapped install.
  //                                  Disabling it is a real reduction in
  //                                  control, not a preference.
  //   PASSWORD_BREACH_FAIL_MODE      what to do when the service cannot be
  //                                  reached: "closed" (default) refuses to set
  //                                  the password; "open" allows it with a loud
  //                                  warning. Closed is right here — setting a
  //                                  password is a rare, interactive, retryable
  //                                  operation.
  PASSWORD_BREACH_CHECK_ENABLED: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  PASSWORD_BREACH_API_URL: z
    .string()
    .url()
    .default("https://api.pwnedpasswords.com/range"),
  PASSWORD_BREACH_FAIL_MODE: z.enum(["closed", "open"]).default("closed"),
  PASSWORD_BREACH_TIMEOUT_MS: z.coerce.number().int().positive().default(4_000),

  // ── Microsoft Entra ID (OIDC Authorization Code + PKCE) ──────────────────
  // Only meaningful when AUTH_MODE=entra. The Fastify API is the confidential
  // OIDC client: it performs discovery, drives the code+PKCE flow, validates
  // the ID token (issuer/audience/tenant/nonce/signature/expiry) and issues its
  // OWN server-side session cookies — Entra's ID/access tokens never reach the
  // browser. All values below are placeholders/empty by default so the app
  // starts in dev/test without Entra; the OIDC login route fails clearly with
  // ENTRA_NOT_CONFIGURED when the required values are absent.
  //
  //   ENTRA_TENANT_ID               Directory (tenant) ID.
  //   ENTRA_CLIENT_ID               Application (client) ID.
  //   ENTRA_CLIENT_SECRET           Confidential-client secret. SECRET — never
  //                                 logged, never returned to the browser.
  //   ENTRA_REDIRECT_URI            Registered callback, e.g.
  //                                 https://ops.yazamco.pro/api/v1/auth/entra/callback
  //   ENTRA_POST_LOGOUT_REDIRECT_URI  Where Entra returns after logout.
  //   ENTRA_ISSUER                  Expected issuer override (else derived from
  //                                 tenant). Validated against the token `iss`.
  //   ENTRA_ALLOWED_TENANT_IDS      CSV of tenant IDs permitted to sign in
  //                                 (defaults to just ENTRA_TENANT_ID).
  //   ENTRA_PROVISIONING_POLICY     first-login policy, applied to every user
  //                                 EXCEPT the very first one. On a deployment
  //                                 where no user holds `super_admin` yet, the
  //                                 first valid Entra sign-in (tenant allowlist
  //                                 enforced first) is provisioned active and
  //                                 granted Super Administrator automatically —
  //                                 the claim is atomic, happens exactly once in
  //                                 the life of a deployment, and needs nothing
  //                                 in the environment. The policy below governs
  //                                 everyone after that:
  //                                   bootstrap — create only bootstrap admins;
  //                                               everyone else must be
  //                                               pre-provisioned/invited.
  //                                   invite    — link only to an existing
  //                                               invited user (by email).
  //                                   open      — auto-create, with the role
  //                                               named by ENTRA_DEFAULT_ROLE.
  //   ENTRA_DEFAULT_ROLE            `roles.key` a newly auto-provisioned account
  //                                 is assigned (policy `open` only). This is
  //                                 the difference between a working account and
  //                                 one that signs in and is then refused by
  //                                 every module: effective access comes from
  //                                 role assignments alone, so an account with
  //                                 none resolves to no access at all. Empty
  //                                 string provisions with NO role, which is a
  //                                 deliberate "approve everyone by hand" stance
  //                                 and not the default.
  //   ENTRA_BOOTSTRAP_ADMINS        CSV of `tenantId:objectId` immutable
  //                                 identities granted Super Administrator on
  //                                 first login. Idempotent; never resets.
  //                                 OPTIONAL — the automatic first-login
  //                                 bootstrap above covers a fresh deployment
  //                                 without it.
  ENTRA_TENANT_ID: z.string().default(""),
  ENTRA_CLIENT_ID: z.string().default(""),
  ENTRA_CLIENT_SECRET: z.string().default(""),
  ENTRA_REDIRECT_URI: z.string().url().or(z.literal("")).default(""),
  ENTRA_POST_LOGOUT_REDIRECT_URI: z.string().url().or(z.literal("")).default(""),
  ENTRA_ISSUER: z.string().url().or(z.literal("")).default(""),
  ENTRA_ALLOWED_TENANT_IDS: z.string().default(""),
  ENTRA_PROVISIONING_POLICY: z
    .enum(["bootstrap", "invite", "open"])
    .default("bootstrap"),
  ENTRA_BOOTSTRAP_ADMINS: z.string().default(""),
  ENTRA_DEFAULT_ROLE: z
    .string()
    .trim()
    .regex(
      /^$|^[a-z][a-z0-9_]*$/,
      "ENTRA_DEFAULT_ROLE must be a role key in lower_snake_case, or empty",
    )
    .default("read_only"),

  // ── GDAP automation (Microsoft 365 onboarding) ──────────────────────────
  // The GDAP workflow reuses the YazamcoOps Entra SSO: the login flow requests
  // the Graph delegated scopes below, and the resulting refresh token is stored
  // AES-256-GCM encrypted with GDAP_TOKEN_ENC_KEY so the API can mint delegated
  // Graph tokens on demand. GDAP is DISABLED unless the key is set. The signing
  // operator must be a Partner Center Admin Agent, and the API app registration
  // must have admin-consented delegated DelegatedAdminRelationship.ReadWrite.All
  // + Group.Read.All. Group.Read.All is the least-privileged delegated permission
  // that supports the only group query (GET /groups?$filter=displayName) —
  // Directory.Read.All would be unnecessarily broad. The key is never logged or
  // returned to the frontend.
  //   GDAP_TOKEN_ENC_KEY  base64-encoded 32-byte key (AES-256-GCM). Empty →
  //                       GDAP automation feature-flagged off. The same key
  //                       encrypts the ONE per-user refresh token the resource
  //                       broker redeems for every Microsoft resource.
  //   GDAP_GRAPH_SCOPES   GDAP's own delegated Graph scopes, appended to the
  //                       OIDC login. Copilot's permissions are NO LONGER mixed
  //                       in here (see COPILOT_* below): the two features are
  //                       unrelated, and requesting GDAP's administrative
  //                       permissions in order to answer a chat question was
  //                       both misleading and more privilege than needed.
  GDAP_TOKEN_ENC_KEY: z.string().default(""),
  GDAP_GRAPH_SCOPES: canonical(GDAP_GRAPH_SCOPES_DEFAULT),

  // ── Microsoft 365 Copilot tab ───────────────────────────────────────────
  // Copilot runs on the same stored per-user refresh token as GDAP (one Entra
  // app, one consent, one encrypted grant) but on its OWN resource and scopes.
  //
  //   COPILOT_API            which API every turn goes to, deployment-wide:
  //                          "graph" (the default) or "workiq". There is NO
  //                          per-user override — the two APIs need different
  //                          admin-consented permissions, so letting a user
  //                          pick meant they could select a resource the tenant
  //                          had never granted and get a consent error no
  //                          reconnect could fix. Nothing else changes either
  //                          way: the service, store, citations and UI are
  //                          shared, and the Work IQ adapter is still built and
  //                          reachable by setting this to "workiq".
  //   COPILOT_WORKIQ_SCOPES  the Work IQ delegated permission. A DIFFERENT
  //                          resource from Graph, so its access token is minted
  //                          separately and a Graph token is never reused for it.
  //   COPILOT_GRAPH_SCOPES   grounding permissions the graph adapter needs.
  //                          Requested at login whenever Copilot is enabled,
  //                          not only when graph is selected: a scope nobody
  //                          consented to cannot be acquired without a fresh
  //                          sign-in, so switching COPILOT_API must not also
  //                          require re-consenting the platform. Work IQ
  //                          grounds itself and needs none.
  //   COPILOT_WORKIQ_BASE_URL  override for a sovereign cloud.
  //   COPILOT_WORKIQ_STREAMING  consume /chatOverStream instead of /chat. The
  //                          answer is still returned in one HTTP response, so
  //                          the frontend is unaffected; this is the switch that
  //                          exercises the streaming path before it is surfaced.
  //   COPILOT_TIMEOUT_MS     per-request upstream timeout.
  COPILOT_API: z.enum(["workiq", "graph"]).default("graph"),
  COPILOT_WORKIQ_SCOPES: canonical(COPILOT_WORKIQ_SCOPES_DEFAULT),
  COPILOT_GRAPH_SCOPES: canonical(COPILOT_GRAPH_SCOPES_DEFAULT),
  // Progressive Graph Copilot snapshots via /chatOverStream. Set false to
  // retain the legacy synchronous /chat transport as a rollback path.
  COPILOT_GRAPH_STREAMING: z
    .enum(["true", "false"])
    .default("true")
    .transform((value) => value === "true"),
  COPILOT_WORKIQ_BASE_URL: canonical(WORK_IQ_BASE_URL_DEFAULT),
  COPILOT_WORKIQ_STREAMING: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  // Content-free, correlation-id-based latency stage logs for Copilot turns.
  // Off by default because this is diagnostic telemetry, not application logic.
  COPILOT_LATENCY_LOGGING: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  COPILOT_TIMEOUT_MS: z.coerce.number().int().min(5_000).max(300_000).default(90_000),

  // ── Enterprise knowledge retrieval ──────────────────────────────────────
  // The Microsoft 365 Copilot Retrieval API (`POST /v1.0/copilot/retrieval`,
  // GA) run BEFORE the chat turn, so an answer about SharePoint documentation
  // is bound to passages the index actually returned rather than to whatever
  // the chat API happened to ground itself in.
  //
  //   COPILOT_KNOWLEDGE_RETRIEVAL  the feature switch, and it defaults to
  //                          FALSE on purpose. Turning it on adds
  //                          `Files.Read.All` to the OIDC login, and a scope
  //                          the tenant has not admin-consented makes SIGN-IN
  //                          itself fail (AADSTS65001) — for every user, not
  //                          just Copilot's. So the order is: grant consent,
  //                          then set this. A deployment that never wants the
  //                          feature is never asked for the permission, which
  //                          is the other half of why this is a flag and not a
  //                          default-on behaviour.
  //   COPILOT_RETRIEVAL_GRAPH_SCOPES  what retrieval needs: `Files.Read.All`
  //                          AND `Sites.Read.All` (Microsoft requires both, for
  //                          OneDrive as well as SharePoint), plus
  //                          `ExternalItem.Read.All` for connector content.
  //                          Kept apart from COPILOT_GRAPH_SCOPES because the
  //                          legacy chat adapter and retrieval are enabled
  //                          independently and neither should drag the other's
  //                          consent along.
  //   COPILOT_KNOWLEDGE_DEFAULT_SCOPE  what a user who has chosen nothing asks
  //                          against. `all` means Work IQ's own enterprise
  //                          grounding with NO retrieval - the behaviour every
  //                          conversation predating this feature ran under, and
  //                          the only scope that really spans Microsoft 365
  //                          rather than just its documents. `sharePoint` and
  //                          `oneDrive` route through the Retrieval API.
  //                          Ignored while the feature is off, when every turn
  //                          is `all` regardless.
  //   COPILOT_RETRIEVAL_TIMEOUT_MS  retrieval's own timeout, necessarily much
  //                          shorter than COPILOT_TIMEOUT_MS: it is spent
  //                          BEFORE the answer is even requested, so it is
  //                          added to the user's wait rather than shared with
  //                          it. A retrieval that overruns degrades to an
  //                          ungrounded turn instead of failing the question.
  //   COPILOT_RETRIEVAL_MAX_QUERIES  ceiling on retrieval calls per data source
  //                          per turn, including the original question.
  //                          Microsoft allows 200 requests per USER per HOUR;
  //                          at the default 3 over two data sources a turn
  //                          costs at most 6, so roughly 33 questions an hour
  //                          before throttling — which is why this is bounded
  //                          rather than agentic.
  COPILOT_KNOWLEDGE_RETRIEVAL: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  COPILOT_RETRIEVAL_GRAPH_SCOPES: canonical(COPILOT_RETRIEVAL_GRAPH_SCOPES_DEFAULT),
  COPILOT_KNOWLEDGE_DEFAULT_SCOPE: z
    .enum(["all", "sharePoint", "oneDrive"])
    .default("all"),
  COPILOT_RETRIEVAL_TIMEOUT_MS: z.coerce
    .number()
    .int()
    .min(2_000)
    .max(120_000)
    .default(30_000),
  COPILOT_RETRIEVAL_MAX_QUERIES: z.coerce.number().int().min(1).max(5).default(3),

  // ── Attaching a file from the operator's own computer ───────────────────
  // Neither Copilot API accepts file BYTES: a turn can only point at something
  // already in OneDrive or SharePoint. So this feature uploads the file into
  // the signed-in user's own OneDrive app folder first, and then attaches it by
  // URI like any other file.
  //
  //   COPILOT_FILE_UPLOAD    the feature switch, FALSE by default for exactly
  //                          the reason retrieval is: turning it on adds a
  //                          Graph scope to the OIDC login, and a scope the
  //                          tenant has not consented makes SIGN-IN itself fail
  //                          (AADSTS65001) for every user of the platform. Grant
  //                          consent first, then set this. With it off, the
  //                          composer offers pasting a SharePoint or OneDrive
  //                          link exactly as it did before.
  //   COPILOT_UPLOAD_GRAPH_SCOPES  `Files.ReadWrite.AppFolder` — write access
  //                          to one application folder in the user's OneDrive,
  //                          not to their drive. Widen it only if a tenant
  //                          turns out not to ground on the app folder.
  //   COPILOT_MAX_UPLOAD_BYTES  hard ceiling per file. Kept at or below Graph's
  //                          4 MB simple-upload limit, because anything larger
  //                          needs an upload SESSION and this deliberately does
  //                          not implement one — a chat attachment is a
  //                          document, not a disk image.
  COPILOT_FILE_UPLOAD: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  COPILOT_UPLOAD_GRAPH_SCOPES: canonical(COPILOT_UPLOAD_GRAPH_SCOPES_DEFAULT),
  COPILOT_MAX_UPLOAD_BYTES: z.coerce
    .number()
    .int()
    .min(64_000)
    .max(4_000_000)
    .default(4_000_000),

  // ── MCP business tools ──────────────────────────────────────────────────
  // Model Context Protocol integrations the Copilot tab may read from,
  // orchestrated BESIDE Work IQ rather than inside it: a Work IQ chat request
  // has no tool channel, so a tool is called first and its result is handed
  // over as `additionalContext`. See docs/M365_COPILOT_MCP.md.
  //
  // Three things live in three places, and keeping them apart is the design:
  //
  //   the CATALOG            in PostgreSQL, `copilot_mcp_integrations`
  //                          (migration 0053), written ONLY through the admin
  //                          API behind `copilot.mcp_integrations_manage`.
  //                          What exists and what is permitted. A RUNTIME
  //                          decision made by an authorized administrator in
  //                          Settings -> Business Integrations. No env var, no
  //                          compose edit, no restart.
  //   APPLICATION secrets    in PostgreSQL, `copilot_mcp_integration_secrets`,
  //                          encrypted with SECRETS_ENC_KEY. An OAuth client
  //                          secret identifies YazamcoOps to a provider and is
  //                          the same for every user. Entered once through the
  //                          GUI and never readable back.
  //   USER credentials       in PostgreSQL, `copilot_mcp_user_connections`,
  //                          encrypted with SECRETS_ENC_KEY, one row per
  //                          (user, integration). The identity every MCP call
  //                          actually runs as. Never in the environment, never
  //                          in a response, never in a log.
  //
  // What remains here is PLATFORM POLICY: ceilings, timeouts and one
  // development flag. None of it is per-integration, and adding Monday or Hudu
  // requires touching none of it.
  //
  //   COPILOT_MCP_SERVERS  *** LEGACY IMPORT ONLY. ***
  //                        The environment catalog this feature used to be
  //                        configured with. The running API does not read it:
  //                        nothing in the request path parses it, and there is
  //                        no merge between it and the database. It is read by
  //                        `pnpm --filter @yazamco/api mcp:import-catalog`,
  //                        which an operator runs ONCE to move an existing
  //                        deployment's integrations into the database, and by
  //                        nothing else.
  //
  //                        Because the old shapes named their credentials by
  //                        variable (`clientIdEnv`, `clientSecretEnv`,
  //                        `credentialEnv`), the importer resolves those names
  //                        against the environment at import time and stores
  //                        the VALUES encrypted. After a successful import,
  //                        delete this variable and the per-integration
  //                        credential variables it named.
  //
  //                        Leaving it set does nothing: it neither enables nor
  //                        configures anything, and an empty database means no
  //                        business tools regardless of its contents.
  //   COPILOT_MCP_OAUTH_REDIRECT_URI  the registered OAuth callback, absolute,
  //                        e.g. https://ops.yazamco.pro/api/v1/copilot/mcp/oauth/callback
  //                        Configuration rather than something derived from the
  //                        request: a redirect_uri built from the Host header
  //                        would let a forged host aim an authorization
  //                        somewhere else, and providers require the value to
  //                        be registered in advance anyway. Empty → derived
  //                        from ENTRA_REDIRECT_URI's origin, which is public by
  //                        definition; `oauth2-user` integrations cannot be
  //                        connected if neither is set.
  //   COPILOT_MCP_MAX_TOOL_CALLS  hard ceiling on MCP calls the orchestrator
  //                        may make for ONE question. 3 by default; it is a
  //                        ceiling and not a target, and most questions make
  //                        zero. 0 switches automatic tool use off entirely
  //                        while leaving connections, preferences and the
  //                        manual retrieval path intact — which is the setting
  //                        for introducing this carefully. Every attempted
  //                        call counts, including later pages of one listing,
  //                        so raising it (up to 10) is how a deployment allows
  //                        deeper bounded pagination.
  //   COPILOT_MCP_MAX_RESULT_BYTES  hard ceiling on what one tool call may
  //                        return, enforced while the body is being read rather
  //                        than after. A server that streams without end is a
  //                        memory problem before it is a prompt problem.
  //   COPILOT_MCP_MAX_CONTEXT_CHARS  ceiling on the evidence one turn's MCP
  //                        blocks contribute to `additionalContext`. Separate
  //                        from the byte cap: that one is about what we are
  //                        willing to RECEIVE, this one about what a chat turn
  //                        can usefully be asked to read.
  //   COPILOT_MCP_CONTEXT_TTL_MINUTES  how long a completed retrieval's bounded
  //                        context survives. Bounded because a retrieval is a
  //                        SNAPSHOT of a business system, and an answer grounded
  //                        in an hour-old ticket queue is worse than one that
  //                        says it could not be grounded.
  //   COPILOT_MCP_OAUTH_STATE_TTL_SECONDS  how long a Connect click stays
  //                        redeemable. Short: a pending state is a capability,
  //                        and somebody who wandered off mid-consent should
  //                        start again rather than leave one lying in a table.
  //   COPILOT_MCP_TOKEN_REFRESH_SKEW_SECONDS  how close to expiry an access
  //                        token is refreshed rather than sent. One round trip
  //                        beats a failed call with somebody's question waiting.
  //   COPILOT_MCP_ALLOW_INSECURE_LOCALHOST  development only: permits an
  //                        http://localhost endpoint or OAuth URL. Production
  //                        requires https, and this flag cannot relax anything
  //                        else — a non-loopback private address is still
  //                        refused either way.
  COPILOT_MCP_SERVERS: z.string().default(""),
  COPILOT_MCP_OAUTH_REDIRECT_URI: z.string().url().or(z.literal("")).default(""),
  COPILOT_MCP_MAX_TOOL_CALLS: z.coerce.number().int().min(0).max(10).default(3),
  COPILOT_MCP_MAX_RESULT_BYTES: z.coerce
    .number()
    .int()
    .min(1_024)
    .max(4_000_000)
    .default(262_144),
  COPILOT_MCP_MAX_CONTEXT_CHARS: z.coerce
    .number()
    .int()
    .min(500)
    .max(60_000)
    .default(12_000),
  COPILOT_MCP_CONTEXT_TTL_MINUTES: z.coerce.number().int().min(1).max(120).default(30),
  COPILOT_MCP_OAUTH_STATE_TTL_SECONDS: z.coerce
    .number()
    .int()
    .min(60)
    .max(1_800)
    .default(600),
  COPILOT_MCP_TOKEN_REFRESH_SKEW_SECONDS: z.coerce
    .number()
    .int()
    .min(30)
    .max(600)
    .default(120),
  COPILOT_MCP_ALLOW_INSECURE_LOCALHOST: z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true"),
  // Exact DNS hostnames that MCP may reach when they resolve to RFC1918/ULA
  // addresses. This is for deliberately co-located services behind split DNS
  // (for example Hudu through a Docker host gateway), not a CIDR bypass.
  COPILOT_MCP_ALLOWED_PRIVATE_HOSTS: z.string().default(""),

  // Where the API redirects the browser after a successful OIDC login (the SPA
  // origin). Empty → same-origin "/".
  //
  // Also the public address used in OUTBOUND mail that must link back to the
  // app (the user invitation email), where a container-internal host would be
  // useless. On a same-origin deployment this is empty, so the link falls back
  // to the origin of ENTRA_POST_LOGOUT_REDIRECT_URI / ENTRA_REDIRECT_URI — both
  // public by definition, since Entra redirects a browser to them.
  FRONTEND_URL: z.string().url().or(z.literal("")).default(""),

  // Strict CORS allowlist (CSV of origins). Empty → same-origin only (no CORS
  // headers emitted). Never use "*" for a cookie-authenticated API.
  CORS_ALLOWED_ORIGINS: z.string().default(""),

  // ── Global HTTP rate limiting (F-10) ─────────────────────────────────────
  // Rate limiting used to exist only on the login endpoint and on API-key
  // traffic. Every other endpoint — including the expensive ones — was
  // unthrottled for a session-authenticated caller, so a single compromised or
  // careless account could exhaust the audit worker pool, hammer n8n and HUDU
  // through the bulk-upsert path, or use the webhook test endpoint as a request
  // amplifier.
  //
  //   RATE_LIMIT_ENABLED    master switch. Leave on.
  //   RATE_LIMIT_MAX        requests per window per key for ordinary endpoints.
  //                         Generous on purpose: this is an abuse ceiling, not a
  //                         quota — the dashboard legitimately fans out to many
  //                         endpoints on load.
  //   RATE_LIMIT_WINDOW_SEC the window length.
  //   RATE_LIMIT_REDIS_URL  when set, buckets live in Redis instead of this
  //                         process's memory. REQUIRED for a correct limit if
  //                         more than one API instance runs: with the in-memory
  //                         store, N instances mean N times the intended
  //                         ceiling. Reuse the Redis that already backs the n8n
  //                         queue; the key prefix below keeps the namespaces
  //                         apart.
  //   RATE_LIMIT_REDIS_PREFIX  key prefix inside Redis.
  RATE_LIMIT_ENABLED: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),

  // ── Internal AI agents ────────────────────────────────────────────────
  // The provider key is optional at boot so administrators can configure the
  // catalogue before enabling chat. Tool calls are denied unless the exact
  // webhook hostname appears in the comma-separated allowlist.
  //
  //   AI_AGENT_KNOWLEDGE_MAX_BYTES  ceiling for one uploaded knowledge FILE.
  //                         Deliberately its own knob rather than reusing
  //                         COPILOT_MAX_UPLOAD_BYTES: that one is pinned to
  //                         Graph's 4 MB simple-upload limit because a chat
  //                         attachment travels through OneDrive, and a
  //                         knowledge file never does — it is read into Postgres
  //                         and never leaves it. The ceiling here is set by
  //                         Postgres instead: `agentKnowledgeFor` ranks with
  //                         `to_tsvector`, which refuses a vector over 1 MB, so
  //                         a document whose text exceeds that would be stored
  //                         and then break retrieval for the whole agent. 2 MB
  //                         of UTF-8 leaves room for a Hebrew document (two
  //                         bytes per character) to still land inside the
  //                         1,000,000-character content limit.
  //                         Raising this past 4 MB also needs the proxy in
  //                         front to agree: see `client_max_body_size` in
  //                         apps/web/nginx.conf, and note that Vercel's
  //                         serverless request body is capped at 4.5 MB and
  //                         cannot be configured at all.
  AI_AGENT_TOOL_ALLOWED_HOSTS: z.string().default(""),
  AI_AGENT_TOOL_TIMEOUT_MS: z.coerce.number().int().min(1000).max(60000).default(15000),
  AI_AGENT_KNOWLEDGE_MAX_BYTES: z.coerce
    .number()
    .int()
    .min(64_000)
    .max(4_000_000)
    .default(2_000_000),
  RATE_LIMIT_MAX: z.coerce.number().int().positive().default(600),
  RATE_LIMIT_WINDOW_SEC: z.coerce.number().int().positive().default(60),
  RATE_LIMIT_REDIS_URL: z.string().default(""),
  RATE_LIMIT_REDIS_PREFIX: z.string().default("yazamcoops:rl:"),
  //   RATE_LIMIT_ALLOW_LOCAL_STORE  acknowledge, in production, that the global
  //                         HTTP limiter is running on the per-process store
  //                         because this is a single-instance deployment.
  //                         Without it production refuses to start unless
  //                         RATE_LIMIT_REDIS_URL is set. (The LOGIN limiter is
  //                         separate and always uses the shared PostgreSQL
  //                         store.)
  RATE_LIMIT_ALLOW_LOCAL_STORE: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),

  // ── Trusted reverse proxy ────────────────────────────────────────────────
  // Which peers may set X-Forwarded-For / X-Forwarded-Proto, i.e. whose claim
  // about the client IP Fastify believes.
  //
  // SECURITY: this used to be `trustProxy: true`, which trusts ANY peer's
  // X-Forwarded-For. `request.ip` is not cosmetic — it is the login rate-limit
  // bucket (`login:<ip>` in auth-session.service.ts) and the IP recorded on
  // every session and auth audit event. With blanket trust, an attacker sends a
  // different forged X-Forwarded-For on each request, gets a fresh rate-limit
  // bucket every time, and the brute-force protection is bypassed while the
  // audit trail records whatever address they chose.
  //
  // Value is passed to Fastify's `trustProxy` as a CSV of IPs and/or CIDRs.
  // The default covers loopback plus the RFC1918 ranges Docker bridge networks
  // are assigned from, because the API is reachable ONLY over the internal
  // Docker networks (its host port publication was removed in the same change).
  // Narrow it to the nginx container's address/subnet if you pin one — the
  // tighter it is, the less the forwarded header is worth to an attacker.
  TRUSTED_PROXY_CIDR: z
    .string()
    .min(1)
    .default("127.0.0.1,::1,10.0.0.0/8,172.16.0.0/12,192.168.0.0/16"),

  // Development-mode local user. Role is validated against the shared role
  // schema, so an invalid DEV_AUTH_ROLE fails application startup.
  DEV_AUTH_USER_ID: z.string().min(1).default("local-admin"),
  DEV_AUTH_EMAIL: z.string().email().default("developer@yazamco.local"),
  DEV_AUTH_DISPLAY_NAME: z.string().min(1).default("Local Administrator"),
  DEV_AUTH_ROLE: applicationRoleSchema.default("Administrator"),

  // ── Browser Agent ───────────────────────────────────────────────────────
  // There is no agent secret here any more. Two used to live in this block and
  // both are gone:
  //
  //   • BROWSER_LAUNCH_SIGNING_KEY, which HMAC-signed the yazamcoops:// launch
  //     URL. It bought nothing the single-use nonce did not already buy — the
  //     agent cannot open a browser until it redeems the launch request, which
  //     matches requestId + nonce atomically against a pending, unexpired row.
  //   • BROWSER_AGENT_API_KEY, one long-lived string held by the API and copied
  //     by hand into every workstation's config.json. It named no session, so
  //     any holder could drive any operator's session, and when a copy drifted
  //     the launches it broke gave no useful signal.
  //
  // A session's credential is now minted with its launch request, handed to the
  // agent inside the launch URL, accepted only for that session, and cleared
  // when the session ends. Workstations store nothing, so there is nothing here
  // to keep in sync with them. See modules/browser-sessions/session-token.ts.

  // ── Browser Agent runtime settings (served to the agent) ────────────────
  // Returned to the agent when it redeems a launch ticket, so a workstation's
  // config.json holds only the API address and its own name. Changing the
  // OAuth client is a change here, not a visit to every machine.
  BROWSER_AGENT_OAUTH_CLIENT_ID: z
    .string()
    .uuid()
    // Microsoft Graph PowerShell — the same public client `Connect-MgGraph` uses.
    .default("14d82eec-204b-4c2f-b7e8-296a70dab67e"),
  BROWSER_AGENT_OAUTH_AUTHORITY: z
    .string()
    .url()
    .default("https://login.microsoftonline.com/organizations"),
  BROWSER_AGENT_OAUTH_TIMEOUT_MS: z.coerce
    .number()
    .int()
    .min(30_000)
    .max(1_800_000)
    .default(300_000),
  // the entire point: a database-only compromise then yields verifiers that
  // cannot be checked or forged offline.
  //
  // Provide >=32 bytes as hex or base64. Empty disables the API-key layer
  // entirely (keys cannot be minted and none are accepted), which is the safe
  // behavior for a deployment that has not configured it yet.
  //
  // Rotating the pepper invalidates every key derived under the previous
  // generation; bump API_KEY_PEPPER_VERSION alongside it so the trail records
  // which generation a key belongs to.
  API_KEY_PEPPER: z.string().default(""),
  API_KEY_PEPPER_VERSION: z.coerce.number().int().positive().default(1),
  // Which key environment this deployment accepts. A `test` key presented to a
  // `live` deployment is rejected outright rather than silently working.
  API_KEY_ENVIRONMENT: z.enum(["live", "test"]).default("live"),
  // Upper bound on how far in the future a key may be set to expire.
  API_KEY_MAX_LIFETIME_DAYS: z.coerce.number().int().positive().max(3650).default(365),

  // ── Dashboard data sources (Phase 5.1) ──────────────────────────────────
  // The dashboard reads REAL operational data from the existing legacy sources
  // (it does not import them into PostgreSQL — the dashboard needs no persistent
  // state). Both are optional: when unset, that source is reported as
  // unavailable and its counts are zero (never fabricated), and the dashboard
  // response is flagged partial.
  //
  //   DASHBOARD_STATE_API_URL   base URL of the legacy state-api. The dashboard
  //                             reads the `dockerServers` key from GET <url>/state
  //                             (Pulseway hardware and Jenkins jobs now come
  //                             from their own DB tables).
  //
  // DASHBOARD_JENKINS_DATA_URL was retired here (F-05): migration 0012 made
  // `jenkins_jobs` authoritative, and the URL only ever pointed at a static file
  // that no longer exists.
  DASHBOARD_STATE_API_URL: z.string().url().or(z.literal("")).default(""),

  // ── Customers integrations (Phase 5.2) ──────────────────────────────────
  // The Customers module preserves the legacy behavior: the company directory
  // and a customer's active services come from HUDU via n8n; the service
  // catalog is a static JSON file; provisioning goes to n8n; result emails go
  // through the SendGrid mailer. All are called SERVER-SIDE from the service
  // layer — the browser no longer talks to these upstreams directly, and the
  // n8n shared secret (previously hardcoded in nginx) now lives here.
  //
  // Each URL is optional: when unset, that upstream is reported as unavailable
  // in `meta` (reads) or a clear 502 (writes) — never fabricated success.
  //
  //   CUSTOMERS_N8N_BASE_URL   base of the n8n webhook host, e.g.
  //                            https://n8n.yazamco.pro/webhook. The module
  //                            appends /hudu-companies, /customers-get,
  //                            /customers-upsert.
  //   CUSTOMERS_N8N_SECRET     the x-techform-secret header value. SECRET —
  //                            never logged, never returned to the client.
  //   CUSTOMERS_SERVICES_URL   full URL to services.json (service catalog).
  //   CUSTOMERS_MAILER_URL     base URL of the SendGrid mailer, e.g.
  //                            http://customers-mailer:3001 (POST <url>/send).
  //   CUSTOMERS_SOURCE_OF_TRUTH  which store backs directory reads. Defaults to
  //                            "legacy" (n8n/HUDU). Switch to "postgres" ONLY as
  //                            an explicit, manual cutover after verification.
  //   CUSTOMERS_JOB_CALLBACK_BASE_URL  the PUBLIC base URL n8n uses to POST the
  //                            provisioning result back to this API, e.g.
  //                            https://ops.yazamco.pro/api/v1/customers/jobs.
  //                            The module appends /<jobId>/result and sends it to
  //                            n8n in the upsert payload as `callbackUrl`. Empty →
  //                            no callbackUrl is sent (n8n must know the URL
  //                            itself); provisioning still runs but the job stays
  //                            "running" until it is reported or the TTL elapses.
  CUSTOMERS_N8N_BASE_URL: z.string().url().or(z.literal("")).default(""),
  CUSTOMERS_N8N_SECRET: z.string().default(""),
  CUSTOMERS_SERVICES_URL: z.string().url().or(z.literal("")).default(""),
  CUSTOMERS_MAILER_URL: z.string().url().or(z.literal("")).default(""),
  //   MAILER_SHARED_SECRET     the `x-mailer-secret` header every request to the
  //                            mailer must carry. REQUIRED, no default. The
  //                            mailer previously had NO authentication: anything
  //                            on the shared n8n-network could send mail as
  //                            YazamcoPro through our SendGrid account. The
  //                            recipient lock limited the damage but did not
  //                            stop it. The mailer itself refuses to start
  //                            without the same value. SECRET — never
  //                            logged/returned.
  MAILER_SHARED_SECRET: z
    .string()
    .min(
      32,
      "MAILER_SHARED_SECRET must be at least 32 characters (set a long random value: openssl rand -base64 48)",
    ),
  CUSTOMERS_SOURCE_OF_TRUTH: z.enum(["legacy", "postgres"]).default("legacy"),
  CUSTOMERS_JOB_CALLBACK_BASE_URL: z
    .string()
    .url()
    .or(z.literal(""))
    .default(""),

  // ── Customer Audits ─────────────────────────────────────────────────────
  // The Customer Audits module runs ENTIRELY inside the API: a submission
  // enqueues one row per customer, an in-process worker claims runs from the
  // database queue, the collectors read the external systems, the audit engine
  // applies the business rules, and the renderer writes the HTML/PDF artifacts
  // to a persistent directory (only the relative path is stored in PostgreSQL).
  // There is no PowerShell, no n8n, no SSH and no inbound callback.
  //
  //   CUSTOMER_AUDITS_STORAGE_PATH  absolute directory (a persistent volume) the
  //                              report artifacts live under. The API writes and
  //                              serves files only from inside this root.
  //   CUSTOMER_AUDITS_WORKER_ENABLED  set false to keep the API request-only
  //                              (queued runs then wait for another instance).
  //   CUSTOMER_AUDITS_WORKER_CONCURRENCY  how many audits may run at once in this
  //                              process (default 2 — audits are I/O heavy and a
  //                              PDF render costs a Chromium page).
  //   CUSTOMER_AUDITS_WORKER_POLL_MS  queue poll interval when idle.
  //   CUSTOMER_AUDITS_MAX_ATTEMPTS  attempts per run before it is failed for good
  //                              (a crashed/abandoned run is re-queued once its
  //                              lease expires).
  //   CUSTOMER_AUDITS_LEASE_TIMEOUT_MS  how long a claimed run may stay silent
  //                              before the lease is considered stale.
  //   CUSTOMER_AUDITS_PDF_ENABLED  render the PDF artifact (needs Chromium in the
  //                              image). False → HTML only; the run still
  //                              succeeds and the UI hides the PDF action.
  CUSTOMER_AUDITS_STORAGE_PATH: z
    .string()
    .default("/var/lib/yazamco/customer-audits"),
  CUSTOMER_AUDITS_WORKER_ENABLED: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  CUSTOMER_AUDITS_WORKER_CONCURRENCY: z.coerce
    .number()
    .int()
    .min(1)
    .max(8)
    .default(2),
  CUSTOMER_AUDITS_WORKER_POLL_MS: z.coerce
    .number()
    .int()
    .min(500)
    .default(3_000),
  CUSTOMER_AUDITS_MAX_ATTEMPTS: z.coerce.number().int().min(1).max(10).default(3),
  CUSTOMER_AUDITS_LEASE_TIMEOUT_MS: z.coerce
    .number()
    .int()
    .min(60_000)
    .default(1_800_000),
  CUSTOMER_AUDITS_PDF_ENABLED: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),

  //   CUSTOMER_AUDITS_SCHEDULE_ENABLED  run the in-process scheduler that fires
  //                              the monthly programme (migration 0020). Whether
  //                              the programme ITSELF is on is a database
  //                              setting an operator controls in the UI; this
  //                              var only decides whether this instance watches
  //                              the clock. Leave it on: the month is claimed in
  //                              the database, so several instances ticking is
  //                              safe and exactly one of them fires.
  //   CUSTOMER_AUDITS_SCHEDULE_POLL_MS  how often to check whether the programme
  //                              is due. This is the granularity of "fires at
  //                              06:00", not a retry interval — a tick is one
  //                              tiny UPDATE plus one SELECT. Default 5 min.
  CUSTOMER_AUDITS_SCHEDULE_ENABLED: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  CUSTOMER_AUDITS_SCHEDULE_POLL_MS: z.coerce
    .number()
    .int()
    .min(10_000)
    .default(300_000),

  // ── Billing vendor APIs (phase 5 adapters) ─────────────────────────────────
  // Every credential below is SERVER-SIDE ONLY: it is read here, validated here,
  // and handed to an adapter that never logs it. None of these reach the browser.
  // A source whose credential is empty is SKIPPED by a snapshot run rather than
  // failing it, so a deployment that does not use a vendor is not permanently red.
  //
  //   ESET. The legacy collector used `grant_type=password` with the password in
  //   the script; that account's password must be rotated. Prefer
  //   BILLING_ESET_REFRESH_TOKEN — when it is set the password grant is never used.
  BILLING_ESET_TOKEN_URL: z
    .string()
    .url()
    .default("https://eu.business-account.iam.eset.systems/oauth/token"),
  BILLING_ESET_API_URL: z
    .string()
    .url()
    .default("https://eu.device-management.eset.systems"),
  BILLING_ESET_REFRESH_TOKEN: z.string().default(""),
  BILLING_ESET_USERNAME: z.string().default(""),
  BILLING_ESET_PASSWORD: z.string().default(""),

  //   Acronis. client_credentials; the Basic header is built from these two, and
  //   the ROOT TENANT ID is read out of the token's scope rather than configured.
  BILLING_ACRONIS_API_URL: z.string().url().default("https://il01-cloud.acronis.com"),
  BILLING_ACRONIS_CLIENT_ID: z.string().default(""),
  BILLING_ACRONIS_CLIENT_SECRET: z.string().default(""),
  //   Usage comes from the platform BILLING REPORT, which generates
  //   asynchronously: the adapter creates a one-shot report, polls until it is
  //   saved, downloads it and deletes it. Generation time scales with the size
  //   of the tenant sub-tree, so both the wait and the poll gap are configurable
  //   rather than baked in. The timeout must stay well under
  //   BILLING_SNAPSHOT_STALE_MS, or a slow report is reclaimed as stale mid-wait.
  BILLING_ACRONIS_REPORT_TIMEOUT_MS: z.coerce.number().int().positive().default(900_000),
  BILLING_ACRONIS_REPORT_POLL_MS: z.coerce.number().int().positive().default(10_000),

  //   SentinelOne. Sent as `Authorization: ApiToken <token>`.
  BILLING_SENTINELONE_API_URL: z
    .string()
    .url()
    .default("https://euce1-spike-it.sentinelone.net"),
  BILLING_SENTINELONE_API_TOKEN: z.string().default(""),
  //   The SentinelOne ACCOUNT whose sites bill as the Cyber Hunter tier (the
  //   other two tiers are decided from the site's own modules, so only this one
  //   needs naming). Compared trimmed and case-insensitively; left blank, the
  //   adapter keeps its built-in default rather than losing the tier entirely.
  BILLING_SENTINELONE_CYBER_HUNTER_ACCOUNT: z
    .string()
    .default("20321 - Yazamco - D180"),

  //   Pulseway. A DIFFERENT API surface from SERVERS_PULSEWAY_* (v3 assets and
  //   organizations, not the per-asset detail the Servers module reads), so it has
  //   its own base URL. The token is the same tenant credential though, so leaving
  //   BILLING_PULSEWAY_API_TOKEN empty falls back to SERVERS_PULSEWAY_API_TOKEN
  //   rather than making the same secret exist twice in the environment.
  BILLING_PULSEWAY_API_URL: z.string().url().default("https://it-m.pulseway.com/api/v3"),
  BILLING_PULSEWAY_API_TOKEN: z.string().default(""),

  //   BILLING_SKU_UPLOAD_MAX_BYTES  hard ceiling on an uploaded Priority price
  //   list. Enforced twice on purpose: @fastify/multipart refuses oversized bytes
  //   at the transport, and the parser refuses them again after assembly, so a
  //   caller that reaches the service by another path is bounded too. A real price
  //   list is well under a megabyte; the default leaves generous headroom while
  //   keeping an .xlsx (a zip) from being used to exhaust memory.
  BILLING_SKU_UPLOAD_MAX_BYTES: z.coerce
    .number()
    .int()
    .min(1024)
    .max(64 * 1024 * 1024)
    .default(15 * 1024 * 1024),

  //   Monthly API snapshots (phase 6). Same shape as the FortiGate and Customer
  //   Audits programmes: a cheap in-process timer that only asks "is anything
  //   due?", with the actual rule — enabled, run hour, business timezone, the
  //   slot claim — living in `billing_snapshot_schedule` (migration 0029), so it
  //   is shared by every API instance and editable without a redeploy. The
  //   business timezone defaults to Asia/Jerusalem on that row; there is no
  //   separate env var for it, to avoid two settings that could disagree.
  //
  //   BILLING_SNAPSHOT_ENABLED  master switch for the timer. Off leaves manual
  //                             snapshot runs fully usable — only the automatic
  //                             last-day-of-month collection stops.
  //   BILLING_SNAPSHOT_POLL_MS  how often the timer asks. Granularity, not
  //                             cadence: it bounds how late a due slot fires,
  //                             never whether it fires.
  //   BILLING_SNAPSHOT_STALE_MS  how long a snapshot may sit in 'running' before
  //                             recovery assumes the collecting instance died
  //                             and marks it 'failed' (never 'closed' — a
  //                             recovered run holds no data). A real collection
  //                             is bounded by the adapters' own per-call timeout
  //                             and page caps, so this is a generous multiple of
  //                             that, not a tight lease.
  BILLING_SNAPSHOT_ENABLED: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  BILLING_SNAPSHOT_POLL_MS: z.coerce
    .number()
    .int()
    .min(30_000)
    .max(3_600_000)
    .default(300_000),
  BILLING_SNAPSHOT_STALE_MS: z.coerce
    .number()
    .int()
    .min(60_000)
    .default(1_800_000),

  //   CUSTOMER_AUDITS_PULSEWAY_TTL_HOURS  how old the shared Pulseway snapshot
  //   may get before an audit run refreshes it. The legacy collector regenerated
  //   PulsewayAssets.txt only when it was not from today, so the default is 24h;
  //   pressing "Sync Pulseway data" in the UI always collects regardless of age.
  CUSTOMER_AUDITS_PULSEWAY_TTL_HOURS: z.coerce
    .number()
    .min(0)
    .max(168)
    .default(24),

  //   CUSTOMER_AUDITS_MONDAY_TTL_HOURS  the same rule for the shared Monday
  //   board snapshot (migration 0022). A board older than this is re-fetched on
  //   first use — read-through, so a skipped sweep never costs a report section —
  //   and "Sync" always re-collects every board regardless of age.
  CUSTOMER_AUDITS_MONDAY_TTL_HOURS: z.coerce
    .number()
    .min(0)
    .max(168)
    .default(24),

  // ── Monday.com (audit data source) ──────────────────────────────────────
  // The audit collectors read the aggregated Microsoft 365 / Active Directory /
  // Intune / FortiGate / Acronis inventories from the existing Monday boards —
  // the same boards the legacy audit scripts read. Board ids are configurable so
  // a board move needs no code change. Blank MONDAY_API_KEY → the Monday
  // collector reports itself unavailable and the affected findings are omitted
  // with an explicit warning (never fabricated).
  //   MONDAY_API_KEY  personal/API token sent as the Authorization header.
  //                   SECRET — never logged, never returned to the client.
  MONDAY_API_KEY: z.string().default(""),
  MONDAY_API_URL: z.string().url().default("https://api.monday.com/v2"),
  MONDAY_BOARD_M365: z.coerce.number().int().default(1639346024),
  MONDAY_BOARD_ACTIVE_DIRECTORY: z.coerce.number().int().default(1898537170),
  MONDAY_BOARD_INTUNE: z.coerce.number().int().default(1754130165),
  MONDAY_BOARD_FORTIGATE: z.coerce.number().int().default(1713800404),
  MONDAY_BOARD_ACRONIS: z.coerce.number().int().default(1631937275),
  MONDAY_BOARD_ACRONIS_DEVICES: z.coerce.number().int().default(1752074595),
  //   MONDAY_BOARD_CUSTOMERS  the "YazamcoPro Customers" board, read ONLY by the
  //   one-time `audits:import-list` script that seeds the monthly programme.
  //   Nothing at runtime reads it: once seeded, YazamcoOps owns the list.
  MONDAY_BOARD_CUSTOMERS: z.coerce.number().int().default(1996291598),

  // ── Dell warranty API (audit data source) ───────────────────────────────
  // Server warranty status comes from Dell's TechDirect entitlements API using
  // an OAuth2 client-credentials token. Blank credentials → warranty is reported
  // as "unknown" with a collector warning; no warranty finding is emitted.
  //   DELL_API_CLIENT_ID / DELL_API_CLIENT_SECRET  SECRET — never logged.
  DELL_API_BASE_URL: z
    .string()
    .url()
    .default("https://apigtwb2c.us.dell.com"),
  DELL_API_CLIENT_ID: z.string().default(""),
  DELL_API_CLIENT_SECRET: z.string().default(""),

  // ── Servers integrations (Phase 5.3) ────────────────────────────────────
  // The Servers module preserves the legacy behavior: the server inventory is
  // the `dockerServers` shared-state key read/written via the state-api; a
  // server's containers come live from WUD (What's Up Docker). Pulseway machine
  // data is now COLLECTED BY THE API ITSELF from the Pulseway REST API (a
  // server-side port of Server_info.ps1) and stored in the DB — there is no
  // longer an n8n trigger or a `servers.json` cache. All upstreams are called
  // SERVER-SIDE from the service layer; the browser never talks to them directly.
  //
  // Each URL is optional: when unset, that upstream is reported as unavailable
  // in `meta` (reads) or a clear 502 (writes) — never fabricated success.
  //
  //   SERVERS_STATE_API_URL     base URL of the legacy state-api. The module
  //                             reads/writes the `dockerServers` key at
  //                             GET/PUT <url>/state/:key.
  //   SERVERS_WUD_BASE_URL      base URL WUD is reached through. Empty string
  //                             means "call the target host directly"
  //                             (http://<ip>:<port>), matching the nginx proxy.
  //   SERVERS_WUD_USERNAME      default WUD Basic-auth username (fallback when a
  //                             server has no per-server credentials).
  //   SERVERS_WUD_PASSWORD      default WUD Basic-auth password/hash. SECRET —
  //                             never logged, never returned to the client.
  //   SERVERS_PULSEWAY_API_URL  base URL of the Pulseway REST API (e.g.
  //                             https://it-m.pulseway.com/api/v3). Empty disables
  //                             Pulseway collection (refresh reports unavailable).
  //   SERVERS_PULSEWAY_API_TOKEN  the raw Pulseway API token sent verbatim as
  //                             `Authorization: Basic <token>`. SECRET — never
  //                             logged, never returned to the client.
  //   SERVERS_PULSEWAY_UPDATES_SCRIPT_ID  Pulseway automation SCRIPT (uid) that
  //                             recomputes a machine's update + security custom
  //                             fields on demand. Run per server by the "Refresh
  //                             update & security fields" action, which waits for
  //                             the execution to finish before re-collecting the
  //                             snapshot. Empty falls back to the tenant's
  //                             built-in id (see pulseway.gateway.ts).
  //   SERVERS_SOURCE_OF_TRUTH   which store backs server-list reads. Defaults to
  //                             "legacy" (state-api). Switch to "postgres" ONLY
  //                             as an explicit, manual cutover after verification.
  //   STATE_API_SHARED_SECRET   the `x-state-api-secret` header every state-api
  //                             request must carry. REQUIRED, no default. The
  //                             state-api previously had NO authentication at
  //                             all: anything that could reach it on the shared
  //                             n8n-network could read the whole shared state
  //                             (including the WUD credentials in
  //                             `dockerServers`) and overwrite any key. The
  //                             state-api itself refuses to start without the
  //                             same value, so there is no "auth disabled" mode
  //                             to fall into. SECRET — never logged/returned.
  //   SECRETS_ENC_KEY           base64-encoded 32-byte key (AES-256-GCM) used to
  //                             encrypt secrets at rest in stores whose schema we
  //                             do not own: the WUD credentials inside the
  //                             state-api's `dockerServers` document and the
  //                             webhook signing secrets in the database. Empty
  //                             disables encryption, and every path that would
  //                             write a new secret then FAILS CLOSED rather than
  //                             writing plaintext; reads still tolerate existing
  //                             plaintext so adopting this needs no downtime.
  //                             Required in production.
  STATE_API_SHARED_SECRET: z
    .string()
    .min(
      32,
      "STATE_API_SHARED_SECRET must be at least 32 characters (set a long random value: openssl rand -base64 48)",
    ),
  SECRETS_ENC_KEY: z.string().default(""),
  SERVERS_STATE_API_URL: z.string().url().or(z.literal("")).default(""),
  SERVERS_WUD_BASE_URL: z.string().url().or(z.literal("")).default(""),
  SERVERS_WUD_USERNAME: z.string().default(""),
  SERVERS_WUD_PASSWORD: z.string().default(""),
  SERVERS_PULSEWAY_API_URL: z.string().url().or(z.literal("")).default(""),
  SERVERS_PULSEWAY_API_TOKEN: z.string().default(""),
  SERVERS_PULSEWAY_UPDATES_SCRIPT_ID: z.string().default(""),
  SERVERS_SOURCE_OF_TRUTH: z.enum(["legacy", "postgres"]).default("legacy"),

  //   SERVERS_ALLOWED_TARGET_HOSTS  which addresses a WUD request may actually be
  //                             sent to. Same comma-separated grammar as
  //                             WEBHOOK_ALLOWED_INTERNAL_HOSTS (hostname / IP /
  //                             IPv4 CIDR).
  //
  // SECURITY (F-5): `fetchContainers` builds `http://<ip>:<port>/api/containers`
  // from an Operator-supplied field and attaches the WUD Basic-auth credentials.
  // Unguarded, that is a request-forgery primitive pointed at anything the API
  // container can reach — the cloud metadata endpoint (169.254.169.254), the
  // API's own loopback, Postgres, the state-api, any internal admin panel — with
  // our credentials attached and the response body relayed back to the caller.
  //
  // Unlike the webhook guard, "publicly routable" is the WRONG rule here: the
  // monitored servers are LAN hosts by definition. So the rule is inverted — the
  // resolved address must be publicly routable OR inside this allowlist, and
  // anything else is refused BEFORE the request is made and before the
  // credentials are attached (fail closed).
  //
  // The default is the three RFC1918 ranges, which is where the monitored fleet
  // actually lives. That keeps existing deployments working while still refusing
  // the addresses that make this dangerous: loopback (127/8, ::1), link-local
  // and the metadata endpoint (169.254/16), CGNAT (100.64/10), IPv6 ULA
  // (fc00::/7), multicast/reserved (224/4+) and 0.0.0.0/8. Narrow it to the
  // exact server subnets for a tighter deployment; an empty value means only
  // publicly routable targets are permitted.
  SERVERS_ALLOWED_TARGET_HOSTS: z
    .string()
    .default("10.0.0.0/8,172.16.0.0/12,192.168.0.0/16"),

  // ── Webhooks (Phase 5.7 + event catalog) ────────────────────────────────
  // Outbound webhook delivery runs server-side and, by default, an SSRF guard
  // blocks any destination that resolves to a non-public address (loopback,
  // link-local/metadata, RFC1918, ULA…). A self-hosted deployment often needs
  // to notify a TRUSTED internal target (n8n, an internal Slack relay, a service
  // on the same Docker network / LAN). This is a comma-separated allowlist that
  // bypasses the block for those destinations only. Each entry is one of:
  //   • a hostname — exact host match (e.g. "n8n", "n8n.internal", "localhost")
  //     — trusted regardless of what it resolves to;
  //   • a literal IP (e.g. "192.168.1.50", "::1");
  //   • an IPv4 CIDR (e.g. "10.0.0.0/8", "192.168.0.0/16") — allow a whole range.
  // Empty (default) keeps the strict public-only guard. Everything NOT matched
  // stays blocked, so this never opens the guard globally.
  WEBHOOK_ALLOWED_INTERNAL_HOSTS: z.string().default(""),
  // Comma-separated process environment names explicitly exposed to
  // notification templates under `environment.<NAME>`. Secret-like names are
  // rejected again at runtime and can never be exposed.
  NOTIFICATION_TEMPLATE_ENV_ALLOWLIST: z.string().default(""),

  // ── Containers integration ──────────────────────────────────────────────
  // The All Containers module polls WUD through the SAME upstreams the Servers
  // module uses (SERVERS_STATE_API_URL / SERVERS_WUD_*) and persists the result
  // in its own tables (migration 0041). The UI reads those tables, so WUD being
  // unavailable costs freshness, not the screen.
  //
  //   CONTAINERS_DOCKERHUB_URL   base URL for the Docker Hub "Latest Published"
  //                              enrichment (default https://hub.docker.com; the
  //                              module appends /v2/repositories/<repo>/tags/<tag>).
  //                              Set empty to disable enrichment entirely. The
  //                              enrichment is opt-in per request and soft-fails
  //                              to a partial — it never blocks or fabricates the
  //                              container list.
  //   CONTAINERS_SYNC_INTERVAL_MS  how often the background sync polls WUD and
  //                              writes `container_inventory` (default 1h). The
  //                              UI reads the inventory, never WUD, so this is
  //                              what determines how fresh the screen is — and
  //                              why the screen still works when WUD is down.
  CONTAINERS_DOCKERHUB_URL: z
    .string()
    .url()
    .or(z.literal(""))
    .default("https://hub.docker.com"),
  CONTAINERS_SYNC_INTERVAL_MS: z.coerce
    .number()
    .int()
    .positive()
    .default(3_600_000),

  // ── Jenkins integration (built-in collection) ───────────────────────────
  // The Jenkins module collects the jobs dataset ITSELF by querying the Jenkins
  // REST API directly (a server-side port of the legacy Jobs.ps1) — there is no
  // external pipeline and no static JSON file. Each sync stores a full snapshot
  // in the jenkins_jobs table (migration 0012); the list endpoint reads from
  // that table. The sync runs on a periodic schedule and on the manual "Update
  // from Jenkins" trigger.
  //
  //   JENKINS_BASE_URL        base URL of the Jenkins server
  //                           (e.g. https://jenkins.yazamco.pro). Empty → the
  //                           collector is disabled: the list reports the source
  //                           as unavailable and the manual trigger returns a
  //                           clear error (never fabricated).
  //   JENKINS_USER            Jenkins username for Basic auth.
  //   JENKINS_API_TOKEN       Jenkins API token for Basic auth (SECRET — never
  //                           logged/returned).
  //   JENKINS_SYNC_INTERVAL_MS  background refresh interval in ms
  //                           (default 6h, matching the legacy pipeline cron).
  JENKINS_BASE_URL: z.string().url().or(z.literal("")).default(""),
  JENKINS_USER: z.string().default(""),
  JENKINS_API_TOKEN: z.string().default(""),
  JENKINS_SYNC_INTERVAL_MS: z.coerce
    .number()
    .int()
    .positive()
    .default(21_600_000),

  // ── Reports integrations (Phase 5.6) ────────────────────────────────────
  // The Reports module preserves the legacy behavior: the reportable systems
  // come from a static services.json; the company directory comes from HUDU via
  // n8n (the SAME contract the Customers module uses); report generation POSTs
  // to the n8n reports-generate webhook. All are called SERVER-SIDE from the
  // service layer — the browser no longer talks to these upstreams directly,
  // and the n8n shared secret (previously hardcoded in nginx) now lives here.
  //
  // Each URL is optional: when unset, reads report the upstream as unavailable
  // in `meta`; generation returns a clear 502 — never fabricated rows.
  //
  //   REPORTS_N8N_BASE_URL   base of the n8n webhook host, e.g.
  //                          https://n8n.yazamco.pro/webhook. The module appends
  //                          /hudu-companies (directory) and /reports-generate.
  //   REPORTS_N8N_SECRET     the x-techform-secret header value. SECRET — never
  //                          logged, never returned to the client.
  //   REPORTS_SYSTEMS_URL    full URL to services.json (the systems catalog).
  REPORTS_N8N_BASE_URL: z.string().url().or(z.literal("")).default(""),
  REPORTS_N8N_SECRET: z.string().default(""),
  REPORTS_SYSTEMS_URL: z.string().url().or(z.literal("")).default(""),

  // ── FortiGate API Check ─────────────────────────────────────────────────
  // A server-side proxy over two n8n webhooks (the browser never calls n8n
  // directly — the webhooks' Header-Auth secrets must not ship in the SPA
  // bundle). The API attaches the auth header here and passes the upstream JSON
  // body through unchanged.
  //
  //   • the CHECK webhook probes ONE device with a supplied address + key. It is
  //     the ad-hoc "Test API" tool and is never persisted.
  //   • the SWEEP webhook enumerates every active Fortinet API asset in HUDU and
  //     probes all of them, returning one row per customer. It is what the daily
  //     scheduled scan calls, and its results ARE stored (migration 0028).
  //
  //   FORTIGATE_N8N_BASE_URL     base of the n8n webhook host, e.g.
  //                              https://n8n.yazamco.pro/webhook. The module
  //                              appends the paths below. Empty → the module is
  //                              unconfigured and the endpoints return a clear
  //                              502 (never fabricated success).
  //   FORTIGATE_N8N_CHECK_PATH   the single-device webhook path
  //                              (default forti-access-check).
  //   FORTIGATE_N8N_SWEEP_PATH   the fleet-sweep webhook path
  //                              (default fortigate-check).
  //   FORTIGATE_N8N_AUTH_HEADER  the Header-Auth header NAME configured on the
  //                              n8n webhook credential (e.g. X-Automation-Key).
  //                              Empty → no auth header is attached.
  //   FORTIGATE_N8N_SECRET       the Header-Auth header VALUE. SECRET — never
  //                              logged, never returned to the client.
  FORTIGATE_N8N_BASE_URL: z.string().url().or(z.literal("")).default(""),
  FORTIGATE_N8N_CHECK_PATH: z.string().default("forti-access-check"),
  FORTIGATE_N8N_SWEEP_PATH: z.string().default("fortigate-check"),
  FORTIGATE_N8N_AUTH_HEADER: z.string().default(""),
  FORTIGATE_N8N_SECRET: z.string().default(""),

  // ── FortiGate monitoring programme ──────────────────────────────────────
  // The daily fleet sweep + the twice-monthly emailed report. Same shape as the
  // Customer Audits programme: a cheap in-process timer whose only job is to ask
  // "is anything due?", with every rule (enabled, hour, days, timezone, the slot
  // claim) living in the database so it is editable from the UI and shared by
  // all API instances.
  //
  //   FORTIGATE_MONITOR_ENABLED  master switch for the timer. Off leaves the
  //                              module fully usable by hand — only the
  //                              automatic sweep and report stop.
  //   FORTIGATE_MONITOR_POLL_MS  how often the timer asks. This is granularity,
  //                              not cadence: it bounds how late a due slot
  //                              fires, never whether it fires.
  FORTIGATE_MONITOR_ENABLED: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  FORTIGATE_MONITOR_POLL_MS: z.coerce
    .number()
    .int()
    .min(30_000)
    .max(3_600_000)
    .default(300_000),

  // ── Hudu integration (M365 Entra app provisioning) ──────────────────────
  // After the Browser Agent provisions the `YazamcoPro Integrations` Entra app
  // for a customer's tenant, the API writes the integration details to Hudu
  // SERVER-SIDE. The API key is read ONLY here — it is never returned to the
  // web app, never sent to the Browser Agent, never written to the database,
  // and never logged. All three are optional so the API still starts without
  // Hudu configured; the provisioning-result endpoint then fails clearly with
  // HUDU_NOT_CONFIGURED and does NOT mark the workflow completed.
  //
  //   HUDU_API_KEY               Hudu API key. SECRET — never logged/returned.
  //   HUDU_BASE_URL              Hudu instance base URL (e.g.
  //                              https://hudu.yazamco.pro).
  //   HUDU_GRAPH_ASSET_LAYOUT_ID Asset layout id used for the Office 365 /
  //                              Graph integration asset (default 42).
  HUDU_API_KEY: z.string().default(""),
  HUDU_BASE_URL: z.string().url().or(z.literal("")).default(""),
  HUDU_GRAPH_ASSET_LAYOUT_ID: z.coerce.number().int().positive().default(42),
  //   HUDU_DOMAIN_ASSET_LAYOUT_ID  asset layout holding the customers' registered
  //                              domains (name + Expirations + Supplier Name).
  //                              Read by the Customer Audits domain collector.
  HUDU_DOMAIN_ASSET_LAYOUT_ID: z.coerce.number().int().positive().default(18),
});

export type ApiEnv = z.infer<typeof apiEnvSchema>;

/**
 * Values AUTH_JWT_SECRET must never hold in production. Each of these is (or
 * was) committed to this repository, so it is public knowledge and provides no
 * signing strength at all. Kept as an explicit set rather than a heuristic so
 * the check can never reject a legitimately random secret.
 */
const KNOWN_PLACEHOLDER_JWT_SECRETS = new Set([
  // The former schema default / docker-compose fallback / .env.example value.
  "dev-only-jwt-secret-change-me-0123456789abcd",
  // The .env.example instruction line, in case it is copied verbatim.
  "<generate: openssl rand -base64 48>",
]);

/**
 * Base64 encoding of exactly 32 bytes: 43 payload characters plus one pad. This
 * package deliberately has no Node type dependency, so the length is checked on
 * the encoded form rather than by decoding it.
 */
const BASE64_32_BYTES = /^[A-Za-z0-9+/]{43}=$/;

/**
 * Parse an environment object, throwing a readable aggregated error on failure.
 */
export function parseApiEnv(
  source: Record<string, string | undefined>,
): ApiEnv {
  const result = apiEnvSchema.safeParse(source);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `  - ${i.path.join(".") || "(root)"}: ${i.message}`)
      .join("\n");
    throw new Error(`Invalid environment configuration:\n${issues}`);
  }
  return result.data;
}

/**
 * Production-only configuration hygiene. Zod validates SHAPE; this validates
 * that a production deployment is not running on development placeholders — or,
 * in the case of AUTH_MODE, on a development code path.
 *
 * Called at startup. It throws rather than warns: a security control that is
 * silently absent is worse than one that was never claimed, and a deployment
 * with an unset agent credential or a missing key pepper is not one that should
 * accept traffic.
 */
export function assertProductionSecrets(env: ApiEnv): void {
  if (env.NODE_ENV !== "production") return;

  const problems: string[] = [];

  // AUTH_MODE=development selects DevelopmentAuthProvider, whose `authenticate`
  // takes no arguments and returns DEV_AUTH_ROLE (default: Administrator) for
  // EVERY request. No token, no cookie, no credential of any kind. It is a
  // deliberate bypass for local work, and in production it is an open door to
  // every integration credential, customer record and admin endpoint this API
  // holds.
  //
  // It is checked here because AUTH_MODE is the one security-relevant variable
  // with a PERMISSIVE default: the schema defaults it to "development" and
  // docker-compose passes `${AUTH_MODE:-development}`, so forgetting the
  // variable — or setting it to the empty string, which `:-` also substitutes
  // for — silently selects the bypass. Every other secret in this function
  // fails closed instead; this one failed open, and shape validation cannot
  // catch it because "development" is a perfectly valid enum member.
  if (env.AUTH_MODE === "development") {
    problems.push(
      "AUTH_MODE is 'development', which authenticates EVERY request as " +
        `'${env.DEV_AUTH_ROLE}' with no credential at all. Production requires ` +
        "AUTH_MODE=entra (Microsoft Entra ID SSO).",
    );
  }

  // AUTH_MODE=local is single-factor by construction: email + password, with no
  // TOTP, no recovery codes and no step-up anywhere in this codebase (search for
  // "totp" — there is nothing). The specification requires MFA for local
  // authentication; rather than claim an MFA control that does not exist, local
  // password authentication is REFUSED in production outright, so the only
  // production login path is Entra, where MFA is the IdP's Conditional Access.
  //
  // This is enforced here — not documented and hoped for — because "local mode
  // is dev-only" is exactly the kind of convention that quietly stops being true.
  // Local auth remains fully available outside production for offline work.
  if (env.AUTH_MODE === "local") {
    problems.push(
      "AUTH_MODE is 'local', which is single-factor (password only — this " +
        "codebase implements no TOTP, recovery codes or step-up auth). " +
        "Production requires AUTH_MODE=entra so that MFA is enforced by " +
        "Microsoft Entra Conditional Access.",
    );
  }

  // The login limiter counts per-IP, per-account and global attempts. Under
  // NODE_ENV=production the store is PostgreSQL (shared across instances); the
  // in-memory store is selected only under NODE_ENV=test. This assertion exists
  // so that stays true: a production instance must have a database URL, which it
  // does by schema, and must not be running the in-memory store.
  if (!env.DATABASE_URL.trim()) {
    problems.push(
      "DATABASE_URL is empty. It backs the SHARED login rate-limit counters " +
        "(rate_limit_counters); without it the limiter cannot be enforced " +
        "consistently across API instances.",
    );
  }

  // The GLOBAL HTTP limiter is a different limiter (plugins/rateLimit.ts) with
  // its own store. Its in-memory store is per-process, so with more than one
  // replica the ceiling is N× the configured value. Redis makes it shared.
  // Single-instance deployments can opt out explicitly.
  if (!env.RATE_LIMIT_ENABLED) {
    problems.push(
      "RATE_LIMIT_ENABLED=false disables global HTTP rate limiting entirely.",
    );
  } else if (!env.RATE_LIMIT_REDIS_URL.trim() && !env.RATE_LIMIT_ALLOW_LOCAL_STORE) {
    problems.push(
      "RATE_LIMIT_REDIS_URL is not set, so global HTTP rate-limit buckets live " +
        "in this process's memory: with more than one API instance the effective " +
        "ceiling is N times RATE_LIMIT_MAX, and every bucket resets on redeploy. " +
        "Set RATE_LIMIT_REDIS_URL, or set RATE_LIMIT_ALLOW_LOCAL_STORE=true to " +
        "acknowledge a single-instance deployment.",
    );
  }

  // The literal that used to be this variable's default (and shipped in
  // .env.example and docker-compose.yml). It is public, so a production
  // instance running on it has forgeable sessions. Length validation cannot
  // catch it — only an exact match can.
  if (KNOWN_PLACEHOLDER_JWT_SECRETS.has(env.AUTH_JWT_SECRET.trim())) {
    problems.push(
      "AUTH_JWT_SECRET is still set to a known placeholder value published in " +
        "this repository. Session tokens signed with it can be forged by " +
        "anyone. Generate a real secret: openssl rand -base64 48",
    );
  }

  // Secrets-at-rest key. Optional in dev (every write path fails closed without
  // it), but a production instance that cannot encrypt is a production instance
  // that cannot save a WUD credential or a webhook secret — fail at startup
  // instead of at the operator's first save.
  if (!env.SECRETS_ENC_KEY) {
    problems.push(
      "SECRETS_ENC_KEY is not set. WUD credentials and webhook secrets cannot " +
        "be encrypted at rest, and every path that would write one fails " +
        "closed. Generate a key: openssl rand -base64 32",
    );
  } else if (!BASE64_32_BYTES.test(env.SECRETS_ENC_KEY.trim())) {
    problems.push(
      "SECRETS_ENC_KEY must decode to exactly 32 bytes (AES-256). Generate " +
        "one with: openssl rand -base64 32",
    );
  }

  // The pepper is optional (it disables the feature), but a SHORT one is always
  // a misconfiguration rather than a deliberate opt-out.
  if (env.API_KEY_PEPPER && env.API_KEY_PEPPER.trim().length < 32) {
    problems.push(
      "API_KEY_PEPPER is set but too short. Provide >=32 bytes of hex or base64.",
    );
  }

  if (problems.length > 0) {
    throw new Error(
      `Refusing to start in production with insecure configuration:\n` +
        problems.map((p) => `  - ${p}`).join("\n"),
    );
  }
}
